import { Icon } from 'Components';
import styles from './RadioCard.module.scss';
import React from 'react';
import { DatePicker, Tooltip } from 'antd';
import moment, { Moment } from 'moment';

export interface RadioCardOption {
	value: string;
	label: string;
	subLabel?: string;
	showDatePicker?: boolean;
	datePlaceholder?: string;
}

interface RadioCardProps {
	id?: string;
	title: string;
	icon?: React.ReactNode;
	tooltip?: string;
	options: RadioCardOption[];
	value: string;
	onChange: (value: string) => void;
	className?: string;
	disabled?: boolean;
}

const RadioCard: React.FC<RadioCardProps> = ({
	id,
	icon,
	title,
	tooltip,
	options,
	value,
	onChange,
	disabled = false
}) => {
	const MAX_SELECTABLE_YEARS_AHEAD = 10;
	const disableInvalidDates = (currentDate: Moment) => {
		const today = moment().startOf('day');
		const maxSelectableDate = moment()
			.add(MAX_SELECTABLE_YEARS_AHEAD, 'years')
			.endOf('day');

		return currentDate < today || currentDate > maxSelectableDate;
	};

	const Heading = () => (
		<div className={styles.heading}>
			<div className={styles.heading__left}>
				{icon}
				<div className={styles.title}>{title}</div>
			</div>
			{tooltip && (
				<Tooltip
					title={tooltip}
					overlayClassName={styles.customTooltip}
				>
					<Icon type="infoOutlined" role="img" />
				</Tooltip>
			)}
		</div>
	);

	return (
		<div id={id} className={styles.container}>
			<Heading />
			<div
				className={`${styles.radioGroup} ${
					disabled ? ` ${styles.disabled}` : ''
				}`}
			>
				{options.map((option) => (
					<div key={option.value}>
						<label key={option.value} className={styles.radioItem}>
							<input
								id={
									id
										? `${id}-option-${option.value}`
										: undefined
								}
								type="radio"
								name={title} // ensures grouping
								value={option.value}
								checked={value === option.value}
								onChange={() => onChange(option.value)}
								className={styles.radioInput}
								disabled={disabled}
							/>
							<span className={styles.radioLabel}>
								{option.label}
							</span>
						</label>
						{option.showDatePicker &&
							option.datePlaceholder &&
							value === option.value && (
								<div className={styles.datePickerWrapper}>
									<DatePicker
										id={
											id
												? `${id}-option-${option.value}-date-picker`
												: undefined
										}
										suffixIcon={
											<Icon
												type="datePickerIcon"
												role="img"
											/>
										}
										placeholder={option.datePlaceholder}
										disabledDate={disableInvalidDates}
									/>
								</div>
							)}
						{option.subLabel && (
							<div className={styles.subLabel}>
								{option.subLabel}
							</div>
						)}
					</div>
				))}
			</div>
		</div>
	);
};

export default RadioCard;
